/** Small, pure formatting helpers. Language-specific words come from i18n, not from here. */

/** "Selin Yılmaz" → "Selin Y." as in the wireframes. */
export function shortName(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length < 2) return displayName.trim();
  const last = parts[parts.length - 1] ?? '';
  return `${parts[0]} ${last.charAt(0).toLocaleUpperCase('tr')}.`;
}

/** Seconds → "m:ss". */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** MIDI pitch → note name with octave, e.g. 55 → "Sol3" with Turkish names. */
export function pitchName(midi: number, names: readonly string[]): string {
  const rounded = Math.round(midi);
  const pc = ((rounded % 12) + 12) % 12;
  return `${names[pc] ?? '?'}${Math.floor(rounded / 12) - 1}`;
}

const ACCIDENTALS: Record<number, string> = { [-2]: '𝄫', [-1]: '♭', 0: '', 1: '♯', 2: '𝄪' };

/** A written note name with accidental and octave, e.g. D, -1, 4 → "Re♭4" with Turkish step names. */
export function spelledName(spelling: { step: string; alter: number; octave: number }, steps: Record<string, string>): string {
  return `${steps[spelling.step] ?? spelling.step}${ACCIDENTALS[spelling.alter] ?? ''}${spelling.octave}`;
}

/**
 * Playback order as runs of consecutive bars: [0,1,2,3,1,2,4,5,6] with numbers "1".."7"
 * → "1–4, 2–3, 5–7".
 */
export function formatPlaybackOrder(order: readonly number[], measureNumbers: readonly string[]): string {
  const runs: string[] = [];
  let start = 0;
  for (let i = 1; i <= order.length; i++) {
    const prev = order[i - 1] as number;
    if (i < order.length && order[i] === prev + 1) continue;
    const first = measureNumbers[order[start] as number] ?? String((order[start] as number) + 1);
    const last = measureNumbers[prev] ?? String(prev + 1);
    runs.push(first === last ? first : `${first}–${last}`);
    start = i;
  }
  return runs.join(', ');
}

export function formatRange(first: string, last: string): string {
  return first === last ? first : `${first}–${last}`;
}

export function formatDate(iso: string, locale = 'tr-TR'): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
}

/** Tempo for display: whole numbers without decimals. */
export function formatBpm(bpm: number): string {
  return Number.isInteger(bpm) ? String(bpm) : bpm.toFixed(1);
}

export function formatDateTime(iso: string, locale = 'tr-TR'): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}
